/**
 * Per-user recent command/record entries for the ⌘K palette.
 * Per-browser, connection-scoped localStorage — same persistence tier as
 * sidebar pins and view prefs.
 */
import { useCallback, useEffect, useState } from "react";
import { prefsStore } from "@/app/preferences/prefsStore";

export interface RecentEntry {
  kind: "record" | "command";
  label: string;
  path: string;
}

const keyFor = (connectionId: string | null | undefined) => `command_recents:${connectionId ?? "none"}`;

const RECENT_CAP = 8;

function load(connectionId: string | null | undefined): RecentEntry[] {
  try {
    const raw = prefsStore.getItem(keyFor(connectionId));
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RecentEntry =>
        Boolean(e) &&
        typeof (e as RecentEntry).kind === "string" &&
        typeof (e as RecentEntry).label === "string" &&
        typeof (e as RecentEntry).path === "string"
    );
  } catch {
    return [];
  }
}

export function useCommandRecents(connectionId: string | null | undefined) {
  const [recents, setRecents] = useState<RecentEntry[]>(() => load(connectionId));

  useEffect(() => {
    setRecents(load(connectionId));
  }, [connectionId]);

  const push = useCallback(
    (entry: RecentEntry) => {
      setRecents((prev) => {
        // Remove existing entry if it exists (by path)
        const filtered = prev.filter((e) => e.path !== entry.path);
        // Prepend the new/updated entry
        const next = [entry, ...filtered].slice(0, RECENT_CAP);
        try {
          prefsStore.setItem(keyFor(connectionId), JSON.stringify(next));
        } catch {
          /* private browsing */
        }
        return next;
      });
    },
    [connectionId]
  );

  return { recents, push };
}
